// Picker order, search and device defaults for the language lists (UI-C2).
import type { UiLang } from "../i18n";
import { primary, readsTraditional } from "../i18n";
import { DEFAULT_CHINESE_READING, READ_LANGUAGES } from "./readLanguages";
import { SPEAK_LANGUAGES, type Language } from "./speakLanguages";

const PINNED = ["ar", "en", "zh"];

const pinRank = (l: Language) => {
  const i = PINNED.indexOf(primary(l.tag));
  return i < 0 ? PINNED.length : i;
};

/** ar, en, zh pinned first (Chinese scripts keep their list order), then the rest alphabetically by the
 * name in the UI language (languageName). */
export function pickerOrder(list: readonly Language[], ui: UiLang): Language[] {
  const collator = new Intl.Collator(ui);
  return list
    .map((l, i) => ({ l, i }))
    .sort((a, b) => {
      const ra = pinRank(a.l);
      const rb = pinRank(b.l);
      if (ra !== rb) return ra - rb;
      if (ra < PINNED.length) return a.i - b.i;
      return collator.compare(languageName(a.l.tag, ui), languageName(b.l.tag, ui));
    })
    .map((x) => x.l);
}

const fold = (s: string) => s.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();

/** Matches the tag, the English, Arabic and native names, and (given `ui`) the name shown in the UI language. */
export function searchLanguages(list: readonly Language[], query: string, ui?: UiLang): Language[] {
  const q = fold(query.trim());
  if (!q) return [...list];
  return list.filter((l) =>
    [l.tag, l.en, l.ar, l.native, ui ? languageName(l.tag, ui) : ""].some((s) => fold(s).includes(q)),
  );
}

export function findLanguage(tag: string): Language | undefined {
  return READ_LANGUAGES.find((l) => l.tag === tag) ?? SPEAK_LANGUAGES.find((l) => l.tag === tag);
}

const displayNames = new Map<string, Intl.DisplayNames | null>();
/** The platform's name of `tag` in `ui`, or undefined (no Intl.DisplayNames, or it knows no name and would return the
 * code). */
export function intlLanguageName(tag: string, ui: string): string | undefined {
  let names = displayNames.get(ui);
  if (names === undefined) {
    try {
      names = typeof Intl.DisplayNames === "function" ? new Intl.DisplayNames([ui], { type: "language", fallback: "none" }) : null;
    } catch {
      names = null; // a tag this browser's Intl refuses: the table's name below
    }
    displayNames.set(ui, names);
  }
  try {
    const name = names?.of(tag);
    return name && name.toLowerCase() !== tag.toLowerCase() ? name : undefined;
  } catch {
    return undefined; // a tag Intl calls malformed
  }
}

/** MAJLIS-164 (ruling 4): a language's name in the interface language. English and Arabic interfaces print the table's
 * own names exactly as before ("Chinese (Mandarin)", the consultant's Arabic); every other interface language asks
 * Intl.DisplayNames in its full tag, and falls back to the table's English name (then the code) when Intl has none. */
export function languageName(tag: string, ui: UiLang): string {
  const row = findLanguage(tag);
  if (ui === "en" || ui === "ar") return row?.[ui] ?? tag;
  return intlLanguageName(tag, ui) ?? row?.en ?? tag;
}

/** Defaults from the device's languages: the first one we can speak / read, else English. */
export function deviceDefaults(deviceLangs: readonly string[]): { speak: string; read: string } {
  let speak: string | undefined;
  let read: string | undefined;
  for (const raw of deviceLangs) {
    const tag = raw.trim();
    if (!tag) continue;
    const p = primary(tag);
    if (!speak && SPEAK_LANGUAGES.some((l) => l.tag === p)) speak = p;
    if (!read) {
      if (p === "zh") read = readsTraditional(tag) ? "zh-Hant" : DEFAULT_CHINESE_READING;
      else if (READ_LANGUAGES.some((l) => l.tag === p)) read = p;
    }
  }
  return { speak: speak ?? "en", read: read ?? "en" };
}

/** A segment in `lang` needs no translation for a reader of `readLang` (same language; M1 does not
 * convert Chinese scripts). */
export function sameLanguage(lang: string, readLang: string): boolean {
  return primary(lang) === primary(readLang);
}
